import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useTranslation } from 'react-i18next';
import { Pin } from '../types';
import { API_BASE_URL } from '../../../config';
import { UpgradeModal } from '../../../components/UpgradeModal';

interface PinPopupProps {
  pin: Pin;
  onClose: () => void;
  onPhotosAdded?: () => void;
}

interface Photo {
  id: string;
  original_key: string;
  created_at: string;
}

function getMarkerColor(count: number): string {
  if (count > 100) return '#f9a825';
  if (count > 50) return '#6a1b9a';
  if (count > 10) return '#1565c0';
  return '#d32f2f';
}

function TeardropMarker({ color, size = 1 }: { color: string; size?: number }) {
  const w = Math.round(16 * size);
  const h = Math.round(22 * size);
  return (
    <svg width={w} height={h} viewBox="0 0 48 64" style={{ marginRight: 4 }}>
      <path
        d="M24 62C24 62 4 36 4 22C4 11 13 2 24 2C35 2 44 11 44 22C44 36 24 62 24 62Z"
        fill={color}
        stroke="#333"
        strokeWidth="2"
      />
      <circle cx="24" cy="22" r="8" fill="white" />
    </svg>
  );
}

function groupByDate(photos: Photo[]): Record<string, Photo[]> {
  const groups: Record<string, Photo[]> = {};
  for (const photo of photos) {
    const date = photo.created_at ? photo.created_at.split('T')[0] : 'unknown';
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(photo);
  }
  return groups;
}

function formatDate(dateStr: string): string {
  if (dateStr === 'unknown') return 'Data desconhecida';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return parts[2] + '/' + parts[1] + '/' + parts[0];
}

export function PinPopup({ pin, onClose, onPhotosAdded }: PinPopupProps) {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState<number>(-1);
  const [deletePhotoId, setDeletePhotoId] = useState<string | null>(null);
  const [showDeletePin, setShowDeletePin] = useState(false);
  const [deletePinConfirmed, setDeletePinConfirmed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/v1/photos/pin/${pin.id}`, { headers: { Authorization: 'Bearer ' + (localStorage.getItem('accessToken') || '') } })
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data.photos) ? data.photos : Array.isArray(data) ? data : []; setPhotos(list);
      })
      .catch(() => {
        setPhotos([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [pin.id]);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const [showUpgrade, setShowUpgrade] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<FileList | null>(null);
  const [photoDate, setPhotoDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [quotaInfo, setQuotaInfo] = useState<{ plan: string; photosUsed: number; photoQuota: number }>({ plan: 'free', photosUsed: 0, photoQuota: 100 });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setPendingFiles(files);
    setPhotoDate(new Date().toISOString().split('T')[0]);
  };

  const handleUploadWithDate = async () => {
    if (!pendingFiles) return;
    const token = localStorage.getItem('accessToken') || '';
    for (let i = 0; i < pendingFiles.length; i++) {
      const formData = new FormData();
      formData.append('file', pendingFiles[i]);
      formData.append('pinId', pin.id);
      formData.append('photoDate', photoDate);
      try {
        const uploadRes = await fetch(`${API_BASE_URL}/api/v1/photos/upload`, { method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: formData });
        if (uploadRes.status === 403) {
          try {
            const statusRes = await fetch(`${API_BASE_URL}/api/v1/billing/status`, { headers: { Authorization: 'Bearer ' + token } });
            if (statusRes.ok) {
              const statusData = await statusRes.json();
              setQuotaInfo({ plan: statusData.plan, photosUsed: statusData.photosUsed, photoQuota: statusData.photoQuota });
            }
          } catch {}
          setShowUpgrade(true);
          setPendingFiles(null);
          return;
        }
      } catch {}
    }
    setPendingFiles(null);
    if (onPhotosAdded) onPhotosAdded();
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/photos/pin/${pin.id}`, { headers: { Authorization: 'Bearer ' + token } });
      const data = await res.json();
      const list = Array.isArray(data.photos) ? data.photos : Array.isArray(data) ? data : [];
      setPhotos(list);
    } catch {}

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const markerColor = getMarkerColor(photos.length);
  const dateGroups = groupByDate(photos);
  const dateKeys = Object.keys(dateGroups).sort().reverse();
  const visitCount = dateKeys.length;

  const legendContent = (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <TeardropMarker color="#d32f2f" size={0.8} />
        <Typography variant="caption">1-10 fotos</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <TeardropMarker color="#1565c0" size={0.8} />
        <Typography variant="caption">11-50 fotos</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <TeardropMarker color="#6a1b9a" size={0.8} />
        <Typography variant="caption">51-100 fotos</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <TeardropMarker color="#f9a825" size={0.8} />
        <Typography variant="caption">101+ fotos</Typography>
      </Box>
    </Box>
  );  return (
    <>
    <Box
      sx={{
        maxWidth: 520,
        maxHeight: 520,
        overflow: 'auto',
        position: 'relative',
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 3,
        p: 2,
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        size="small"
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
      >
        <CloseIcon />
      </IconButton>

      {/* Header */}
      <Box sx={{ pr: 4, mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {pin.title}
        </Typography>
        {pin.city && (
          <Typography variant="body2" color="text.secondary">
            {pin.city}
          </Typography>
        )}
        {pin.visitDate && (
          <Typography variant="caption" color="text.secondary">
            Visita: {pin.visitDate}
          </Typography>
        )}
      </Box>

      {/* Stats area */}
      {!loading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            p: 1,
            bgcolor: 'grey.50',
            borderRadius: 1,
          }}
        >
          <Tooltip title={legendContent} arrow placement="bottom">
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <svg width="16" height="22" viewBox="0 0 48 64" style={{ marginRight: 4 }}>
                <path
                  d="M24 62C24 62 4 36 4 22C4 11 13 2 24 2C35 2 44 11 44 22C44 36 24 62 24 62Z"
                  fill={markerColor}
                  stroke="#333"
                  strokeWidth="2"
                />
                <circle cx="24" cy="22" r="8" fill="white" />
              </svg>
            </Box>
          </Tooltip>
          <Chip label={`${photos.length} fotos`} size="small" variant="outlined" />
          <Chip
            label={visitCount + ' visitas'}
            size="small"
            variant="outlined"
          />
          <Button size="small" startIcon={<AddPhotoAlternateIcon />} onClick={handleUploadClick} variant="contained" sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.75rem' }}>
            + Fotos
          </Button>
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Photo timeline */}
      {!loading && dateKeys.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {dateKeys.map((dateKey) => (
            <Box key={dateKey} sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}
              >
                {formatDate(dateKey)}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  overflowX: 'auto',
                  pb: 1,
                  '&::-webkit-scrollbar': { height: 4 },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'grey.300',
                    borderRadius: 2,
                  },
                }}
              >
                {dateGroups[dateKey].map((p, idx) => (
                  <Box key={p.id} sx={{ position: 'relative', display: 'inline-block' }}>
                    <Box
                      component="img"
                      onClick={() => setLightboxIdx(photos.findIndex(ph => ph.id === p.id))} src={(p.url || '/uploads/' + (p.original_key || '').split('/').pop())}
                      sx={{
                        width: 110,
                      height: 110,
                      minWidth: 110,
                      objectFit: 'cover',
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: 'grey.200',
                    }}
                      alt=""
                    />
                    <Tooltip title="Excluir" arrow><IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeletePhotoId(p.id); }} sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', width: 22, height: 22, '&:hover': { bgcolor: 'error.main' } }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Empty state */}
      {!loading && photos.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Nenhuma foto adicionada ainda.
          </Typography>
        </Box>
      )}

      {/* Upload button moved to stats area */}
      {/* Delete Pin Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 1 }}>
        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => { setShowDeletePin(true); setDeletePinConfirmed(false); }}>
          Excluir Pin
        </Button>
      </Box>

      {/* Delete Pin Confirmation Dialog */}
      <Dialog open={showDeletePin} onClose={() => setShowDeletePin(false)} maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Excluir Pin e Todas as Fotos
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ao excluir este pin, todas as fotos associadas serão permanentemente removidas. Esta ação não pode ser desfeita.
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={deletePinConfirmed} onChange={(e) => setDeletePinConfirmed(e.target.checked)} color="error" />}
            label={<Typography variant="body2">Confirmo que desejo excluir definitivamente este pin e todas as suas fotos</Typography>}
          />
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, pt: 0 }}>
          <Button onClick={() => setShowDeletePin(false)} variant="outlined" size="small">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            disabled={!deletePinConfirmed}
            onClick={async () => {
              const token = localStorage.getItem('accessToken');
              const res = await fetch('/api/v1/locations/pins/' + pin.id, { method: 'DELETE', headers: token ? { Authorization: 'Bearer ' + token } : {} });
              if (res.ok) {
                setShowDeletePin(false);
                onClose();
              }
            }}
          >
            Excluir Definitivamente
          </Button>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletePhotoId} onClose={() => setDeletePhotoId(null)} maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Excluir Foto
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Tem certeza que deseja excluir esta foto? Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, pt: 0 }}>
          <Button onClick={() => setDeletePhotoId(null)} variant="outlined" size="small">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={async () => {
              const token = localStorage.getItem('accessToken');
              const res = await fetch(`${API_BASE_URL}/api/v1/photos/${deletePhotoId}`, { method: 'DELETE', headers: token ? { Authorization: 'Bearer ' + token } : {} });
              if (res.ok) setPhotos(prev => prev.filter(ph => ph.id !== deletePhotoId));
              setDeletePhotoId(null);
            }}
          >
            Excluir
          </Button>
        </Box>
      </Dialog>

      {/* Lightbox with navigation */}
      <Dialog open={lightboxIdx >= 0} onClose={() => setLightboxIdx(-1)} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '85vh' }}>
          <IconButton onClick={() => setLightboxIdx(-1)} sx={{ position: 'absolute', top: 8, right: 8, color: 'white', zIndex: 1 }}><CloseIcon /></IconButton>
          {lightboxIdx > 0 && (
            <IconButton onClick={() => setLightboxIdx(lightboxIdx - 1)} sx={{ position: 'absolute', left: 8, color: 'white', zIndex: 1 }}><ChevronLeftIcon sx={{ fontSize: 40 }} /></IconButton>
          )}
          {lightboxIdx < photos.length - 1 && (
            <IconButton onClick={() => setLightboxIdx(lightboxIdx + 1)} sx={{ position: 'absolute', right: 48, color: 'white', zIndex: 1 }}><ChevronRightIcon sx={{ fontSize: 40 }} /></IconButton>
          )}
          {lightboxIdx >= 0 && photos[lightboxIdx] && (
            <Box component="img" src={(photos[lightboxIdx]?.url || (photos[lightboxIdx]?.original_key ? '/uploads/' + photos[lightboxIdx].original_key.split('/').pop() : ''))} sx={{ maxWidth: '90vw', maxHeight: '82vh', objectFit: 'contain', display: 'block', mx: 'auto' }} />
          )}
          <Box sx={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'white' }}>{lightboxIdx + 1} / {photos.length}</Typography>
            <Button startIcon={<DownloadIcon />} variant="contained" size="small" onClick={() => { const a = document.createElement('a'); a.href = (photos[lightboxIdx]?.original_key ? (photos[lightboxIdx].url || '/uploads/' + photos[lightboxIdx].original_key.split('/').pop()) : ''); a.download = 'photo.jpg'; a.click(); }}>Download</Button>
          </Box>
        </DialogContent>
      </Dialog>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </Box>

      {/* Date picker dialog for photo upload */}
      <Dialog open={!!pendingFiles} onClose={() => setPendingFiles(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('photos.dateTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('photos.dateDescription', { count: pendingFiles?.length || 1 })}
          </Typography>
          <TextField
            type="date"
            value={photoDate}
            onChange={(e) => setPhotoDate(e.target.value)}
            fullWidth
            inputProps={{ max: new Date().toISOString().split('T')[0] }}
          />
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, pt: 0 }}>
          <Button onClick={() => setPendingFiles(null)} variant="outlined" size="small">{t('common.cancel')}</Button>
          <Button onClick={handleUploadWithDate} variant="contained" size="small">{t('photos.upload')}</Button>
        </Box>
      </Dialog>

      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        currentPlan={quotaInfo.plan}
        photosUsed={quotaInfo.photosUsed}
        photoQuota={quotaInfo.photoQuota}
      />
      </>
  );

}
